/**
 * I collegamenti del widget "Azioni rapide".
 *
 * Sono quelli che la vecchia pagina iniziale mostrava come riquadri, piu'
 * qualche sezione arrivata dopo (Lead, Analisi, Mailing...). Ognuno compare
 * solo a chi puo' davvero aprire quella pagina: i ruoli sono quelli delle
 * rotte in App.tsx. Di partenza si vedono i sette di prima; nelle
 * impostazioni del widget ognuno sceglie i suoi.
 *
 * Il colore di "Attivita'" non e' piu' il rosso: in Pixel il rosso vuol dire
 * pericolo o scaduto.
 */

import type { UserRole } from '../../types/roles';

export interface Collegamento {
  id: string;
  titolo: string;
  descrizione: string;
  href: string;
  ruoli: readonly UserRole[];
  /** Classe del riquadro dell'icona. */
  colore: string;
}

const VENDITE: readonly UserRole[] = ['admin', 'sales', 'commerciale', 'amministrazione'];
const AMMINISTRATIVI: readonly UserRole[] = ['admin', 'commerciale', 'amministrazione'];

export const COLLEGAMENTI: readonly Collegamento[] = [
  { id: 'clienti', titolo: 'Clienti', descrizione: 'Gestisci i tuoi clienti e le loro informazioni', href: '/customers', ruoli: VENDITE, colore: 'bg-primary-500' },
  { id: 'fatture', titolo: 'Fatture', descrizione: 'Crea e gestisci le fatture clienti', href: '/invoices', ruoli: AMMINISTRATIVI, colore: 'bg-slate-700' },
  { id: 'prodotti', titolo: 'Prodotti', descrizione: 'Il catalogo dei prodotti e dei servizi', href: '/products', ruoli: [...VENDITE, 'lettore'], colore: 'bg-green-500' },
  { id: 'offerte', titolo: 'Offerte', descrizione: 'Crea e gestisci le offerte personalizzate', href: '/price-lists', ruoli: VENDITE, colore: 'bg-purple-500' },
  { id: 'calendario', titolo: 'Calendario', descrizione: 'Appuntamenti, chiamate e promemoria', href: '/calendar', ruoli: VENDITE, colore: 'bg-yellow-500' },
  { id: 'attivita', titolo: 'Attività', descrizione: 'Le attività e le interazioni con i clienti', href: '/activities', ruoli: VENDITE, colore: 'bg-orange-500' },
  { id: 'abbonamenti', titolo: 'Subscriptions', descrizione: 'Le sottoscrizioni mensili e i rinnovi dei clienti', href: '/subscriptions', ruoli: VENDITE, colore: 'bg-lime-500' },
  { id: 'lead', titolo: 'Lead', descrizione: 'I contatti arrivati da Meta, Google e dal sito', href: '/leads', ruoli: VENDITE, colore: 'bg-violet-500' },
  { id: 'analisi', titolo: 'Analisi aziendali', descrizione: 'Fatturato, incassi e costi', href: '/analisi-aziendali', ruoli: AMMINISTRATIVI, colore: 'bg-emerald-600' },
  { id: 'passivita', titolo: 'Passività', descrizione: 'Le fatture dei fornitori', href: '/passive-invoices', ruoli: AMMINISTRATIVI, colore: 'bg-pink-500' },
  { id: 'provvigioni', titolo: 'Provvigioni', descrizione: 'Le provvigioni maturate e pagate', href: '/commissions', ruoli: AMMINISTRATIVI, colore: 'bg-amber-500' },
  { id: 'mailing', titolo: 'Mailing', descrizione: 'Campagne e sequenze email', href: '/mailing/campagne', ruoli: ['admin', 'commerciale'], colore: 'bg-fuchsia-500' },
];

/** I sette riquadri della vecchia pagina iniziale. */
export const COLLEGAMENTI_DI_PARTENZA = ['clienti', 'fatture', 'prodotti', 'offerte', 'calendario', 'attivita', 'abbonamenti'];

/** Tutti quelli che il ruolo puo' aprire: sono le scelte nelle impostazioni. */
export function collegamentiDisponibili(ruolo: UserRole | null | undefined): Collegamento[] {
  return ruolo ? COLLEGAMENTI.filter((c) => c.ruoli.includes(ruolo)) : [];
}

/**
 * Quelli da mostrare: i scelti, nell'ordine scelto, o quelli di partenza se
 * la scelta e' vuota. Un collegamento che il ruolo non puo' aprire non compare.
 */
export function collegamentiDaMostrare(ruolo: UserRole | null | undefined, scelti: readonly string[]): Collegamento[] {
  const disponibili = new Map(collegamentiDisponibili(ruolo).map((c) => [c.id, c]));
  const ids = scelti.length > 0 ? scelti : COLLEGAMENTI_DI_PARTENZA;
  return ids.map((id) => disponibili.get(id)).filter((c): c is Collegamento => !!c);
}
